import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../firebase/AuthContext';
import Container from 'react-bootstrap/Container';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import { BsList } from 'react-icons/bs';
import '../styles/components/logo.css';
import '../styles/components/navbar.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../App.css';
import { db } from '../firebase/firebaseConfig';
import { doc, getDoc, Timestamp } from 'firebase/firestore';
import { clearNextBillingFieldsIfPastDueIndex0 } from './payments/PayPalSubscriptionService';
import AlertModal from './payments/server/alertModal';

const TRIAL_ACTIVE_SS = 'phn:trialActiveSession';
const SUB_ACTIVE_SS = 'phn:subActiveSession';
const SIGN_IN_PATH = '/sign-in';

function NavbarComponent() {
  const [expanded, setExpanded] = useState(false);
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const navbarRef = useRef(null);
  const [errorMessage, setErrorMessage] = useState('');

  const isBelowLg = useIsBelowLg();

  const [trialActive, setTrialActive] = useState(false);
  const [subscriptionActive, setSubscriptionActive] = useState(false);

  const isSameOrigin = (url) => {
    try {
      const parsedUrl = new URL(url, window.location.href);
      return parsedUrl.origin === window.location.origin;
    } catch {
      return false;
    }
  };

  const toAppPath = (url) => {
    const parsedUrl = new URL(url, window.location.href);
    return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
  };

  const safeNavigateOrOpen = (url, { replace = false } = {}) => {
    if (isSameOrigin(url)) {
      const path = toAppPath(url);
      if (replace) {
        navigate(path, { replace: true });
      } else {
        navigate(path);
      }
    } else {
      window.location.assign(url);
    }
  };

  const getAiAppsUrl = () => {
    const isDev = (() => {
      if (
        typeof window !== 'undefined' &&
        /^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname)
      ) {
        return true;
      }
      if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development') {
        return true;
      }
      return false;
    })();

    return isDev
      ? 'http://localhost:3000/ai-assistant'
      : 'https://purehealthnexus.com/ai-assistant';
  };

  useEffect(() => {
    let cancelled = false;

    async function readAccessStatus() {
      setTrialActive(false);
      setSubscriptionActive(false);

      if (!currentUser) {
        try {
          sessionStorage.removeItem(TRIAL_ACTIVE_SS);
          sessionStorage.removeItem(SUB_ACTIVE_SS);
        } catch {}
        return;
      }

      try {
        const reference = doc(db, 'users', currentUser.uid);
        const snapshot = await getDoc(reference);

        if (cancelled) {
          return;
        }

        if (!snapshot.exists()) {
          setTrialActive(false);
          setSubscriptionActive(false);
          try {
            sessionStorage.removeItem(TRIAL_ACTIVE_SS);
            sessionStorage.removeItem(SUB_ACTIVE_SS);
          } catch {}
          return;
        }

        const data = snapshot.data() || {};

        const endTrialTimestamp =
          data.endTrialPeriodDate instanceof Timestamp
            ? data.endTrialPeriodDate
            : null;
        const isActiveTrialFlag = data.isTrialPeriodActive === true;

        let isTrialActive = false;
        if (endTrialTimestamp && isActiveTrialFlag) {
          const now = new Date();
          const end = endTrialTimestamp.toDate();
          isTrialActive = end > now;
        }
        setTrialActive(isTrialActive);

        const subscriptionsArray = Array.isArray(data.subscriptions)
          ? data.subscriptions
          : [];
        const activeSubscription =
          subscriptionsArray.find((subscription) => subscription?.isActive) ||
          subscriptionsArray[0] ||
          null;
        const status = String(activeSubscription?.status || '').toUpperCase();
        const statusActive = ['ACTIVE', 'TRIALING', 'APPROVAL_PENDING'].includes(
          status
        );
        const pausedOrCancelled =
          status === 'SUSPENDED' || status === 'CANCELLED';
        const nextBillingTimestamp =
          activeSubscription?.nextBillingTime instanceof Timestamp
            ? activeSubscription.nextBillingTime
            : null;
        const nextBillingDate = nextBillingTimestamp
          ? nextBillingTimestamp.toDate()
          : null;
        const nextBillingFuture =
          nextBillingDate && nextBillingDate > new Date();
        const nextBillingPast =
          nextBillingDate && nextBillingDate <= new Date();

        const resolvedSubscriptionActive = Boolean(
          activeSubscription &&
            ((statusActive && (!nextBillingDate || nextBillingFuture)) ||
              (pausedOrCancelled && nextBillingFuture))
        );

        setSubscriptionActive(resolvedSubscriptionActive);

        if (currentUser?.uid && pausedOrCancelled && nextBillingPast) {
          try {
            await clearNextBillingFieldsIfPastDueIndex0(db, currentUser.uid);
          } catch {}
        }

        try {
          if (isTrialActive) {
            sessionStorage.setItem(TRIAL_ACTIVE_SS, '1');
          } else {
            sessionStorage.removeItem(TRIAL_ACTIVE_SS);
          }

          if (resolvedSubscriptionActive) {
            sessionStorage.setItem(SUB_ACTIVE_SS, '1');
          } else {
            sessionStorage.removeItem(SUB_ACTIVE_SS);
          }
        } catch {}

      } catch {
        if (!cancelled) {
          setTrialActive(false);
          setSubscriptionActive(false);
          setErrorMessage('Unable to check your trial or subscription access. Please try again later.');
        }
      }
    }

    readAccessStatus();

    return () => {
      cancelled = true;
    };
  }, [currentUser, location.pathname]);

  const handleAuthClick = async () => {
    if (currentUser) {
      try {
        await logout();
      } catch {
        setErrorMessage('Unable to sign out. Please try again.');
        return;
      }

      try {
        sessionStorage.removeItem(TRIAL_ACTIVE_SS);
        sessionStorage.removeItem(SUB_ACTIVE_SS);
      } catch {
        setErrorMessage('You signed out, but temporary access information could not be cleared from this session.');
      }

      setExpanded(false);
      navigate('/', { replace: true });
    } else {
      setExpanded(false);
      navigate(SIGN_IN_PATH);
    }
  };

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        expanded &&
        navbarRef.current &&
        !navbarRef.current.contains(event.target)
      ) {
        setExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [expanded]);

  const handleNavClick = (label) => {
    if (window.ppms && window.ppms.tm) {
      window.ppms.tm.api('trackEvent', 'Navigation', 'Click', label);
    }
  };

  const AuthButton = ({ className = '', style }) => (
    <button
      className={`button ${currentUser ? 'button-danger' : ''} ${className}`}
      style={style}
      onClick={handleAuthClick}
      aria-label={currentUser ? 'Sign Out' : 'Sign In'}
    >
      <span className="btn-label">
        {currentUser ? 'Sign Out' : 'Sign In'}
      </span>
    </button>
  );

  const sessionTrialActive =
    typeof window !== 'undefined' &&
    sessionStorage.getItem(TRIAL_ACTIVE_SS) === '1';
  const sessionSubActive =
    typeof window !== 'undefined' &&
    sessionStorage.getItem(SUB_ACTIVE_SS) === '1';

  const hasTrialAccess = trialActive || sessionTrialActive;
  const hasSubscriptionAccess = subscriptionActive || sessionSubActive;

  const showAiAvatar = !!currentUser && (hasTrialAccess || hasSubscriptionAccess);

  const aiUserName =
    currentUser?.displayName ||
    (currentUser?.email ? currentUser.email.split('@')[0] : '');

  const onClickAiAvatar = (event) => {
    event.preventDefault();
    const url = getAiAppsUrl();
    handleNavClick('AI Apps');
    setExpanded(false);
    safeNavigateOrOpen(url);
  };

  const onClickHealthTools = () => {
    setExpanded(false);
    safeNavigateOrOpen('https://purehealthnexus.com/apps');
  };

  const onClickBlog = () => {
    setExpanded(false);
    safeNavigateOrOpen('https://purehealthnexus.com/blog');
  };

  // Product navigation items with modern styles and analytics labels.
  const productNavItems = [
    /*
    {
      path: '/top-rated-vitamins',
      label: 'Quality Vitamins',
      eventLabel: 'Quality Vitamins',
    },
    {
      path: '/ashwagandha',
      label: 'Ashwagandha',
      eventLabel: 'Ashwagandha',
    },
    {
      path: '/lionsmane',
      label: "Lion's Mane",
      eventLabel: "Lion's Mane",
    },
    {
      path: '/weightloss',
      label: 'Weight Loss',
      eventLabel: 'Weight Loss',
    },
    {
      path: '/best-whey-protein',
      label: 'Sport Nutrition',
      eventLabel: 'Sport Nutrition',
    },
    {
      path: '/workout-gear',
      label: 'Workout Gear',
      eventLabel: 'Workout Gear',
    },
    {
      path: '/best-home-gym',
      label: 'Home Gym',
      eventLabel: 'Home Gym',
    },
    */
  ];

  // Prebuild nav links so we can reorder them dynamically based on screen size.
  const productNavLinks = productNavItems.map((item) => (
    <Nav.Link
      key={item.path}
      as={Link}
      to={item.path}
      className="nav-item-modern"
      active={location.pathname === item.path}
      onClick={() => {
        handleNavClick(item.eventLabel);
        setExpanded(false);
      }}
    >
      {item.label}
    </Nav.Link>
  ));

  const healthToolsButtonElement = (
    <button
      key="health-tools-button"
      className="navbar-utility-button navbar-utility-button--apps"
      onClick={onClickHealthTools}
      title="Apps"
    >
      <span className="navbar-utility-button__icon">🛠️</span>
      <span className="navbar-utility-button__label">
        Health Tools
      </span>
    </button>
  );

  const blogButtonElement = (
    <button
      key="blog-button"
      className="navbar-utility-button navbar-utility-button--blog"
      onClick={onClickBlog}
    >
      <span className="navbar-utility-button__label">Nutrition Blog</span>
    </button>
  );

  return (
    <>
    <Navbar
      ref={navbarRef}
      expand="lg"
      data-bs-theme="light"
      expanded={expanded}
      onToggle={setExpanded}
      className="custom-navbar"
      fixed="top"
    >
      <Container className="d-flex justify-content-between align-items-center">
        <Navbar.Brand as="div" className="ms-0 brand-with-ai">
          <Link to="/" className="brand-logo-link" aria-label="Home">
            <img
              src="/assets/images/logo.png"
              alt="Pure Health Nexus Logo"
              className="logo"
            />
          </Link>

          {showAiAvatar && (
            <div className="ai-avatar-wrap" aria-label="AI Apps">
              <button
                type="button"
                className="ai-avatar-link"
                onClick={onClickAiAvatar}
                title="Open Ai Health Apps"
                aria-label="Open Ai Health Apps"
              >
                <img
                  src="/assets/images/Ai.png"
                  alt="AI Apps"
                  className="ai-avatar-img"
                  draggable="false"
                />
              </button>

              {aiUserName && (
                <span className="phn-ai-username" aria-live="polite">
                  Hi, {aiUserName}
                </span>
              )}
            </div>
          )}
        </Navbar.Brand>

        <div className="toggler-collapse-container d-flex align-items-center">
          <Navbar.Toggle
            aria-controls="basic-navbar-nav"
            className="border-0"
            type="button"
          >
            <BsList size={30} />
          </Navbar.Toggle>

          {(!currentUser || !isBelowLg) && (
            <AuthButton style={{ marginLeft: '0.5rem' }} />
          )}

          <Navbar.Collapse id="basic-navbar-nav" className="overlay-collapse">
            <Nav className="overlay-nav">
              {isBelowLg ? (
                <>
                  {/* Mobile: Health Tools and Blog first, then the rest */}
                  {healthToolsButtonElement}
                  {blogButtonElement}
                  {productNavLinks}
                </>
              ) : (
                <>
                  {/* Desktop: keep original order (products first, then utilities) */}
                  {productNavLinks}
                  {healthToolsButtonElement}
                  {blogButtonElement}
                </>
              )}

              {/* Mobile-only sign out stays at the bottom of the list */}
              {currentUser && isBelowLg && (
                <AuthButton className="nav-signout-button" />
              )}
            </Nav>
          </Navbar.Collapse>
        </div>
      </Container>
    </Navbar>
    <AlertModal
      open={Boolean(errorMessage)}
      title="Account error"
      message={errorMessage}
      variant="error"
      requester={{ entityId: 'navbar.auth' }}
      onClose={() => setErrorMessage('')}
      primaryAction={{ label: 'Close', onClick: () => setErrorMessage('') }}
    />
    </>
  );
}

function useIsBelowLg() {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 991.98px)');
    const updateMatches = (event) => setMatches(event.matches);

    updateMatches(media);

    if (media.addEventListener) {
      media.addEventListener('change', updateMatches);
    } else {
      media.addListener(updateMatches);
    }

    return () => {
      if (media.removeEventListener) {
        media.removeEventListener('change', updateMatches);
      } else {
        media.removeListener(updateMatches);
      }
    };
  }, []);

  return matches;
}

export default NavbarComponent;